import { V2ApiError } from '../../services/v2';

const MAIL_FIELD_LABELS: { [key: string]: string } = {
    SMTP: 'SMTP 邮件协议',
    PROVIDER_API: '服务商 API',
    FAKE: '模拟通道',
    SSL_TLS: 'SSL/TLS',
    STARTTLS: 'STARTTLS',
    MANUAL: '手工添加',
    IMPORT: '文件导入',
    MIXED: '混合来源',
    STANDALONE: '独立发送',
    NURTURE: '培育计划发送',
    ORIGINAL: '原始批次',
    MANUAL_RETRY: '人工重发',
    AUTO: '自动重试',
    RECONCILIATION: '对账处理',
    RFC_MESSAGE_ID: '邮件消息标识精确匹配',
    REFERENCES: '邮件引用关系匹配',
    PROVIDER_THREAD: '服务商会话匹配',
    SENDER_WINDOW: '发件人与时间窗匹配',
    RATE_LIMIT: '发送频率受限',
    PROVIDER_TEMPORARY: '邮件服务商临时故障',
    NETWORK_NOT_SENT: '网络异常且邮件未提交',
    AMBIGUOUS_SUBMISSION: '提交结果无法确认',
    AUTHENTICATION: '邮箱认证失败',
    PERMISSION_OR_QUOTA_FINAL: '权限不足或额度已用尽',
    CONTENT_REJECTED: '邮件内容被拒绝',
    ADDRESS_PERMANENT: '收件地址永久无效',
    POLICY_DENIED: '策略拒绝发送',
    MAILBOX_NOT_FOUND: '邮箱不存在',
    RATE_LIMITED: '发送过频被运营商阻拦',
    MAILBOX_FULL: '收件邮箱容量已满',
    POLICY_BLOCKED: '被邮件运营商策略阻拦',
    TEMPORARY_OTHER: '其他临时退信',
    PERMANENT_OTHER: '其他永久退信',
    EMAIL_FORMAT_INVALID: '邮箱格式无效',
    EMAIL_PAUSED: '邮箱已暂停使用',
    EMAIL_UNVERIFIED: '邮箱尚未验证',
    UNSUBSCRIBED: '用户已退订',
    EMPLOYMENT_ENDED: '联系人任职关系已结束',
    COMPANY_RESTRICTED: '企业限制触达',
    GLOBAL_SUPPRESSION: '已进入全局抑制名单',
    POLICY_REVIEW_REQUIRED: '需要人工复核触达资格',
    MANUAL_EXCLUDED: '人工排除',
    DAILY_QUOTA_EXHAUSTED: '当日发送额度已用尽',
    MAIL_RATE_LIMITED: '发送频率受限',
    SMTP_AUTHENTICATION: 'SMTP 认证失败',
    SMTP_CREDENTIAL_UNAVAILABLE: 'SMTP 凭据不可用，请重新保存并验证账号',
    SMTP_MESSAGE_BUILD_FAILED: '邮件内容或发件字段无法构造',
    SMTP_SUBMISSION_UNKNOWN: 'SMTP 提交结果未知',
    PROVIDER_UNAVAILABLE: '邮件服务暂不可用',
    EMPTY_PROVIDER_RESULT: '邮件服务未返回结果',
    TEMPLATE_DATA_MISSING: '模板所需数据不完整',
    TEST_PREPARATION_FAILED: '测试邮件准备失败',
    PRE_SUBMIT_DEFERRED: '提交前延迟等待',
    PRE_SUBMIT_FAILURE: '提交前检查失败',
    ACCEPTED_WITHOUT_REFERENCE: '邮件已受理但缺少服务商引用',
    FINAL_FAILURE_NOT_ACCEPTED: '邮件未受理且不可重试',
    RETRYABLE_FAILURE_NOT_ACCEPTED: '邮件未受理，等待重试',
    UNCLASSIFIED_PROVIDER_ERROR: '未分类的邮件服务错误',
    TRANSPORT_CONFIG_REQUIRED: '需要补充收件通道配置',
    CREDENTIAL_REENTRY_REQUIRED: '需要重新录入邮箱密码或授权码',
    RECONCILED_NOT_FOUND: '对账后仍未找到服务商记录',
    WORKER_LEASE_EXPIRED: '发送任务执行租约已过期',
    PRE_SUBMIT_POLICY_DENIED: '提交前资格复查未通过',
    BATCH_CANCELLED: '批次已取消',
    USER_UNSUBSCRIBED: '用户已退订',
    IMAP_SYNC_FAILED: '收件箱连接或同步失败',
    BOUNCE_MAILBOX_NOT_FOUND: '收件邮箱不存在',
    BOUNCE_RATE_LIMITED: '发送过频，已加入自动重试队列',
    BOUNCE_RATE_LIMIT_RETRY_EXHAUSTED: '发送过频，但自动重试次数已用尽',
};

const TEMPLATE_ANALYSIS_LABELS: { [key: string]: string } = {
    PENDING: '待检查',
    SCANNING: '检查中',
    READY: '检查通过',
    BLOCKED: '检查未通过',
    FAILED: '检查失败',
    LEGACY_ACCEPTED: '历史检查通过',
};

const TEMPLATE_TEST_LABELS: { [key: string]: string } = {
    NOT_TESTED: '未测试',
    PASSED: '已通过',
    FAILED: '失败',
    LEGACY_ACCEPTED: '历史通过',
};

export const mailFieldLabel = (value?: string, fallback = '—') =>
    value ? MAIL_FIELD_LABELS[value] || fallback : fallback;

export const templateAnalysisStatusLabel = (value?: string) =>
    value ? TEMPLATE_ANALYSIS_LABELS[value] || '未知检查状态' : '待上传';

export const templateTestStatusLabel = (value?: string) =>
    value ? TEMPLATE_TEST_LABELS[value] || '未知测试状态' : '未测试';

export const localizedMailError = (message?: string, code?: string, category?: string) => {
    const categoryLabel = mailFieldLabel(category);
    if (categoryLabel !== '—') return categoryLabel;
    const codeLabel = mailFieldLabel(code);
    if (codeLabel !== '—') return codeLabel;
    if (message && /[\u3400-\u9fff]/.test(message)) return message;
    return message ? '邮件处理失败，请联系管理员查看详细日志' : '—';
};

export const validateMailRate = (value: unknown, maximum: unknown) => {
    const rate = Number(value);
    const limit = Number(maximum);
    if (!Number.isInteger(rate) || rate < 1 || !Number.isInteger(limit) || rate > limit) {
        return `请输入 1 到 ${Number.isInteger(limit) && limit >= 1 ? limit : 1} 之间的整数`;
    }
    return undefined;
};

export const MAIL_CAPABILITY_CODES = new Set([
    'NOT_FOUND',
    'HTTP_404',
    'ENDPOINT_NOT_FOUND',
    'METHOD_NOT_ALLOWED',
]);

export const capabilityMessage = (error: V2ApiError, capability: string) => {
    if (error.status === 404 || error.status === 405 || MAIL_CAPABILITY_CODES.has(error.code)) {
        return `${capability}前端已经按设计契约接入，但当前服务端尚未启用对应接口。`;
    }
    return error.message;
};

export const replyStatusOf = (row: any) => {
    if (row.replyStatus) return row.replyStatus;
    if (row.engagementStatus === 'HUMAN_REPLIED') return 'HUMAN_REPLIED';
    if (row.engagementStatus === 'AUTO_REPLIED') return 'AUTO_REPLIED';
    return 'NONE';
};

export const hasHumanReply = (row: any) =>
    row.hasHumanReply === true
    || row.hasHumanReply === 1
    || replyStatusOf(row) === 'HUMAN_REPLIED';

export const loadRecent = <T>(key: string): T[] => {
    try {
        const value = JSON.parse(window.localStorage.getItem(key) || '[]');
        return Array.isArray(value) ? value : [];
    } catch (_) {
        return [];
    }
};

export const saveRecent = <T extends { [key: string]: any }>(
    key: string,
    item: T,
    identity: (value: T) => string
) => {
    const current = loadRecent<T>(key);
    const next = [item, ...current.filter((value) => identity(value) !== identity(item))].slice(0, 20);
    window.localStorage.setItem(key, JSON.stringify(next));
    return next;
};

export const toIsoDateTime = (value?: string) =>
    value ? new Date(value).toISOString() : undefined;

export const blockRemoteImages = (html: string) => {
    if (!html || typeof DOMParser === 'undefined') return html;
    const documentNode = new DOMParser().parseFromString(html, 'text/html');
    const configuredServiceBase = process.env.REACT_APP_BASE_API
        || (process.env.NODE_ENV === 'production'
            ? 'https://www.heartyconsulting.com/crmservice'
            : 'http://localhost:8080/crmservice');
    const allowedAssetOrigins = new Set<string>([window.location.origin]);
    try {
        allowedAssetOrigins.add(new URL(configuredServiceBase, window.location.href).origin);
    } catch (_) {
        // A malformed build-time API URL simply means only same-origin assets are trusted.
    }
    const trustedAssetOrigins = new Set<string>();
    Array.from(documentNode.querySelectorAll('img')).forEach((image) => {
        const source = image.getAttribute('src') || '';
        if (!/^https?:\/\//i.test(source)) return;
        try {
            const url = new URL(source, window.location.href);
            if (allowedAssetOrigins.has(url.origin)
                && url.pathname.startsWith('/crmservice/public/mail-template-images/')) {
                trustedAssetOrigins.add(url.origin);
                return;
            }
        } catch (_) {
            // Invalid URLs are removed below together with untrusted remote images.
        }
        image.setAttribute('data-blocked-remote-src', source);
        image.removeAttribute('src');
    });
    const policy = documentNode.createElement('meta');
    policy.setAttribute('http-equiv', 'Content-Security-Policy');
    policy.setAttribute(
        'content',
        `default-src 'none'; img-src data: cid: ${Array.from(trustedAssetOrigins).join(' ')}; style-src 'unsafe-inline'; font-src 'none'; media-src 'none'; frame-src 'none'; connect-src 'none'`
    );
    documentNode.head.prepend(policy);
    Array.from(documentNode.querySelectorAll('img')).forEach((image) => {
        image.removeAttribute('srcset');
    });
    return `<!doctype html>${documentNode.documentElement.outerHTML}`;
};

export const downloadEmailImportTemplate = () => {
    const blob = new Blob(['\uFEFFemail\r\n'], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'mail-recipient-import-template.csv';
    anchor.click();
    URL.revokeObjectURL(url);
};
